/** Estimated tax payments and IRS or state notices: the client's Tax tab panels and the firm-wide pages. */
import { ESTIMATE_AUTHORITIES, formatCents, IRS_NOTICES, NOTICE_AUTHORITIES, noticeResponseDate, parseDollars } from "@clientflow/shared";
import { ruleStatusLabel, type RuleStatusView } from "@/components/rule-settings";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { ask } from "@/components/dialogs";
import { DocumentPicker } from "@/components/document-picker";
import { Empty, ListSearch, Loading, matches, PageHeader, Pager, Section, ShowMore, StatRow, usePaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, defaultTaxYear, formatDate, localToday } from "@/lib/format";
import { useMemberNames, useMembers } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";
import type { Doc } from "@/lib/types";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
const pill = "rounded-full px-2.5 py-0.5 text-xs font-medium";
const tone = {
  wait: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  late: "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200",
  done: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
  off: "bg-muted text-muted-foreground",
};
const jurisdictionName = (j: string) => (j === "federal" ? "Federal" : j);
const money = (s: string) => (s.trim() === "" ? null : parseDollars(s));
const dollars = (c: number | null | undefined) => (c === null || c === undefined ? "" : (c / 100).toFixed(2));

// --- Estimated payments ------------------------------------------------------------------

export type EstimatePayment = { id: string; installment: number; dueOn: string; amountCents: number; paidOn: string | null; paidCents: number | null; confirmation: string; reminderSentAt?: string | null };
type Plan = {
  id: string;
  taxYear: number;
  jurisdiction: string;
  marriedFilingSeparately: boolean;
  priorYearTaxCents: number | null;
  priorYearAgiCents: number | null;
  priorYearFullYear: boolean;
  projectedTaxCents: number | null;
  withholdingCents: number;
  requiredAnnualCents: number;
  basis: "prior_year" | "current_year" | "custom";
  notes: string;
  sharedAt: string | null;
  authority: { name: string; form: string; payUrl: string; payLabel: string; source: string };
  payments: EstimatePayment[];
  /** Maryland and Virginia: the firm's state holiday list behind the dates, and whether the client sees them as estimates. */
  holidays?: RuleStatusView | null;
  datesEstimated?: boolean;
};
type Preview = { safeHarbor: { requiredAnnualCents: number; basis: string; priorYearPct: number | null; estimatesCents: number; installments: number[]; underThreshold: boolean } | null; dueDates: string[] };

/** Where an installment stands. */
export function paymentStatus(p: EstimatePayment, today: string): [string, string] {
  if (p.paidOn) return [`Paid ${formatDate(p.paidOn)}`, tone.done];
  if (p.amountCents === 0) return ["Nothing due", tone.off];
  if (p.dueOn < today) return ["Past due", tone.late];
  if (daysBetween(today, p.dueOn) <= 14) return [p.dueOn === today ? "Due today" : `Due in ${daysBetween(today, p.dueOn)} day${daysBetween(today, p.dueOn) === 1 ? "" : "s"}`, tone.wait];
  return ["Upcoming", tone.off];
}

function PlanForm({ clientId, plan, onDone }: { clientId: string; plan?: Plan; onDone: () => void }) {
  const [v, setV] = useState({
    taxYear: String(plan?.taxYear ?? defaultTaxYear() + 1),
    jurisdiction: plan?.jurisdiction ?? "federal",
    mfs: plan?.marriedFilingSeparately ?? false,
    prior: dollars(plan?.priorYearTaxCents),
    agi: dollars(plan?.priorYearAgiCents),
    fullYear: plan?.priorYearFullYear ?? true,
    projected: dollars(plan?.projectedTaxCents),
    withholding: dollars(plan?.withholdingCents ?? 0),
    notes: plan?.notes ?? "",
  });
  const body = () => ({
    taxYear: Number(v.taxYear),
    jurisdiction: v.jurisdiction,
    marriedFilingSeparately: v.mfs,
    priorYearTaxCents: money(v.prior),
    priorYearAgiCents: money(v.agi),
    priorYearFullYear: v.fullYear,
    projectedTaxCents: money(v.projected),
    withholdingCents: money(v.withholding) ?? 0,
    notes: v.notes,
  });
  let valid = /^\d{4}$/.test(v.taxYear);
  try {
    body();
  } catch {
    valid = false;
  }
  const { data: preview } = useQuery<Preview>({
    queryKey: ["estimate-preview", JSON.stringify(v)],
    queryFn: () => api.send("POST", "/api/tax/estimates/preview", body()),
    enabled: valid,
  });
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/clients/${clientId}/tax/estimates`, body()), onSuccess: onDone });
  const a = ESTIMATE_AUTHORITIES[v.jurisdiction]!;
  const sh = preview?.safeHarbor;
  const field = (k: "prior" | "agi" | "projected" | "withholding", label: string, hint?: string) => (
    <div className="space-y-1">
      <Label htmlFor={`ef-${k}`}>{label}</Label>
      <Input id={`ef-${k}`} inputMode="decimal" placeholder="0.00" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="ef-year">Tax year</Label>
          <Input id="ef-year" inputMode="numeric" value={v.taxYear} disabled={!!plan} onChange={(e) => setV({ ...v, taxYear: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ef-j">Pay to</Label>
          <select id="ef-j" className={select} value={v.jurisdiction} disabled={!!plan} onChange={(e) => setV({ ...v, jurisdiction: e.target.value })}>
            {Object.values(ESTIMATE_AUTHORITIES).map((x) => (
              <option key={x.code} value={x.code}>
                {x.name} ({x.form})
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input type="checkbox" checked={v.mfs} onChange={(e) => setV({ ...v, mfs: e.target.checked })} /> Married filing separately
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("prior", "Last year's total tax", "From the prior-year return (total tax, before payments).")}
        {field("agi", "Last year's AGI", v.jurisdiction === "federal" ? "Over $150,000 ($75,000 separately) uses 110% of last year." : undefined)}
        {field("projected", "This year's projected tax (optional)", "From a projection or tax plan; 90% of it may be lower.")}
        {field("withholding", "Expected withholding this year")}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!v.fullYear} onChange={(e) => setV({ ...v, fullYear: !e.target.checked })} /> Last year was a short year (the prior-year test can't be used)
      </label>
      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
        {!sh ? (
          <p className="text-muted-foreground">Enter last year's tax or this year's projection to see the safe harbor.</p>
        ) : (
          <div className="space-y-2">
            <p>
              Safe harbor: <span className="font-semibold tabular">{formatCents(sh.requiredAnnualCents)}</span> for the year (
              {sh.basis === "prior_year" ? `${sh.priorYearPct}% of last year's tax` : `${a.currentYearPct}% of this year's projected tax`}), less withholding:{" "}
              <span className="font-semibold tabular">{formatCents(sh.estimatesCents)}</span> in estimates.
            </p>
            <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {sh.installments.map((c, i) => (
                <li key={i} className="rounded border border-border bg-card px-2 py-1.5">
                  <span className="block text-xs text-muted-foreground">{formatDate(preview!.dueDates[i])}</span>
                  <span className="font-medium tabular">{formatCents(c)}</span>
                </li>
              ))}
            </ol>
            {sh.underThreshold && <p className="text-xs text-muted-foreground">The balance after withholding is under the {a.name}'s penalty threshold; estimates may not be required.</p>}
            <p className="text-xs text-muted-foreground">Rule: {a.source}.</p>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor="ef-notes">Notes for the firm</Label>
        <Textarea id="ef-notes" rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
      </div>
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={!sh || save.isPending}>
          {plan ? "Recalculate plan" : "Save plan"}
        </Button>
      </div>
    </form>
  );
}

function PaymentRow({ p, canEdit, today }: { p: EstimatePayment; canEdit: boolean; today: string }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(dollars(p.amountCents));
  const save = useMutation({ meta: { inlineError: true }, mutationFn: (body: Record<string, unknown>) => api.send("PATCH", `/api/tax/estimate-payments/${p.id}`, body), onSuccess: () => setEditing(false) });
  const [label, cls] = paymentStatus(p, today);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5">
      <span className="w-20 text-xs text-muted-foreground">Payment {p.installment}</span>
      <span className="w-28 text-sm tabular">{formatDate(p.dueOn)}</span>
      {editing ? (
        <span className="flex items-center gap-2">
          <Input aria-label="Amount" inputMode="decimal" className="h-8 w-28" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Button size="sm" onClick={() => save.mutate({ amountCents: money(amount) ?? 0 })} disabled={save.isPending}>
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAmount(dollars(p.amountCents));
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </span>
      ) : (
        <span className="w-28 text-sm font-medium tabular">{formatCents(p.amountCents)}</span>
      )}
      <span className={`${pill} ${cls}`}>{label}</span>
      {p.paidOn && p.paidCents !== null && p.paidCents !== p.amountCents && <span className="text-xs text-muted-foreground">paid {formatCents(p.paidCents)}</span>}
      {p.confirmation && <span className="text-xs text-muted-foreground">#{p.confirmation}</span>}
      {canEdit && (
        <span className="ml-auto flex gap-1">
          {!p.paidOn && !editing && (
            <Button size="sm" variant="ghost" onClick={() => (setAmount(dollars(p.amountCents)), setEditing(true))}>
              Change amount
            </Button>
          )}
          {p.paidOn ? (
            <Button size="sm" variant="ghost" onClick={() => save.mutate({ paidOn: null })}>
              Undo paid
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => save.mutate({ paidOn: p.dueOn < today ? p.dueOn : today })}>
              Mark paid
            </Button>
          )}
        </span>
      )}
      {save.error && <span className="w-full text-xs text-destructive">{errorMessage(save.error)}</span>}
    </li>
  );
}

/** A client's estimate plans: the safe harbor, the four installments, sharing with the client. */
type SetAside = {
  year: number;
  businesses: Array<{ clientId: string; name: string; ownershipPct: string | null; line: string; profitCents: number | null; closedMonths: number; plannedMonths: number; filledIn: boolean; shareDollars: number | null; note: string | null }>;
  estimatePlanId: string | null;
  plan: { id: string; title: string; scenarioName: string | null } | null;
  projection: { planTaxCents: number; forecastTaxCents: number; lines: Array<{ line: string; planDollars: number; forecastDollars: number }> } | null;
  suggestion: {
    requiredAnnualCents: number;
    basis: "prior_year" | "current_year";
    withholdingCents: number;
    estimatesCents: number;
    paidCents: number;
    remainingCents: number;
    installments: Array<{ id: string; installment: number; dueOn: string; currentCents: number; amountCents: number }>;
    monthlySetAsideCents: number;
    monthsLeft: number;
  } | null;
};

/**
 * The owner's estimates against their accepted tax plan, projected again with their share of their
 * businesses' forecast profit this year. Shown once the client owns a business or has an accepted plan.
 */
function SetAsidePanel({ clientId, canEdit }: { clientId: string; canEdit: boolean }) {
  const { data } = useQuery<SetAside>({ queryKey: [`/api/clients/${clientId}/tax/set-aside`] });
  const apply = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/clients/${clientId}/tax/set-aside/apply`, {}) });
  if (!data || (!data.plan && !data.businesses.length)) return null;
  const sg = data.suggestion;
  const changes = sg?.installments.filter((x) => x.amountCents !== x.currentCents) ?? [];
  const dollars = (d: number) => formatCents(d * 100, { showCents: false });
  return (
    <Section title={`${data.year} set-aside from the CFO forecast`} description={data.plan ? `The accepted plan “${data.plan.title}”${data.plan.scenarioName ? ` (${data.plan.scenarioName})` : ""}, projected again with the client's share of their businesses' forecast profit.` : "The client has no accepted tax plan for this year, so there is nothing to project yet."}>
      {data.businesses.length > 0 && (
        <TableScroll label="Set-aside by business table">
          <table className="w-full min-w-[36rem] text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-1.5 pr-3 text-left font-medium">Business</th>
                <th className="py-1.5 pr-3 text-right font-medium">Share</th>
                <th className="py-1.5 pr-3 text-right font-medium">Profit for {data.year}</th>
                <th className="py-1.5 text-right font-medium">Client's share</th>
              </tr>
            </thead>
            <tbody>
              {data.businesses.map((b) => (
                <tr key={b.clientId} className="border-b border-border align-top last:border-0">
                  <td className="py-1.5 pr-3">
                    <Link href={`/clients/${b.clientId}`} className="font-medium hover:underline">
                      {b.name}
                    </Link>
                    <span className="block text-xs text-muted-foreground">
                      {b.line}
                      {b.profitCents !== null && ` · ${b.closedMonths} closed month${b.closedMonths === 1 ? "" : "s"}${b.plannedMonths ? `, ${b.plannedMonths} from the CFO plan` : ""}${b.filledIn ? "; the rest filled in proportionally" : ""}`}
                    </span>
                    {b.note && <span className="block text-xs text-amber-700 dark:text-amber-300">{b.note}</span>}
                  </td>
                  <td className="tabular py-1.5 pr-3 text-right">{b.ownershipPct === null ? "—" : `${Number(b.ownershipPct).toLocaleString("en-US", { maximumFractionDigits: 4 })}%`}</td>
                  <td className="tabular py-1.5 pr-3 text-right">{b.profitCents === null ? "—" : formatCents(b.profitCents, { showCents: false })}</td>
                  <td className="tabular py-1.5 text-right">{b.shareDollars === null ? "—" : dollars(b.shareDollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {data.projection && sg && (
        <>
          <div className="mt-4">
          <StatRow
            stats={[
              { label: "Federal tax, accepted plan", value: formatCents(data.projection.planTaxCents, { showCents: false }) },
              { label: "With the forecast", value: formatCents(data.projection.forecastTaxCents, { showCents: false }) },
              { label: "Still to pay in estimates", value: formatCents(sg.remainingCents, { showCents: false }) },
              { label: "Set aside each month", value: formatCents(sg.monthlySetAsideCents, { showCents: false }) },
            ]}
          />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {data.projection.lines.map((l) => `${l.line}: ${dollars(l.planDollars)} in the plan, ${dollars(l.forecastDollars)} from the forecast. `).join("")}
            Still to pay is spread over {sg.monthsLeft === 1 ? "this month" : `the ${sg.monthsLeft} months to the last installment`}. Required for the year {formatCents(sg.requiredAnnualCents)} ({sg.basis === "prior_year" ? "the prior-year safe harbor" : "90% of this year's projected tax"}), less withholding {formatCents(sg.withholdingCents)} and {formatCents(sg.paidCents)} already paid. Federal income tax only; Social Security and Medicare on an owner's salary are paid through payroll.
          </p>
          {!data.estimatePlanId ? (
            <p className="mt-3 text-sm text-muted-foreground">Set up the {data.year} federal estimated payments to compare the installments.</p>
          ) : sg.installments.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Every {data.year} federal installment is paid.</p>
          ) : (
            <div className="mt-3 rounded-md border border-border">
              <ul className="divide-y divide-border text-sm">
                {sg.installments.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-1.5">
                    <span className="min-w-0 flex-1">
                      Installment {x.installment} · due {formatDate(x.dueOn)}
                    </span>
                    <span className="tabular text-muted-foreground">{formatCents(x.currentCents)}</span>
                    <span aria-hidden className="text-muted-foreground">→</span>
                    <span className={cn("tabular font-medium", x.amountCents !== x.currentCents && "text-primary")}>{formatCents(x.amountCents)}</span>
                  </li>
                ))}
              </ul>
              {canEdit && (
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-3 py-2">
                  {apply.error && <p className="mr-auto text-sm text-destructive">{errorMessage(apply.error)}</p>}
                  <Button
                    size="sm"
                    disabled={!changes.length || apply.isPending}
                    onClick={async () => {
                      if (await ask({ title: "Change the remaining installments?", description: `The ${changes.length === 1 ? "unpaid installment changes" : `${changes.length} unpaid installments change`} to the amounts shown. Paid installments stay as they are. If the schedule is shared, the client is told by email.`, confirmLabel: "Change installments" })) apply.mutate();
                    }}
                  >
                    {changes.length ? "Use these amounts" : "Installments match"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

export function EstimatesPanel({ clientId, me }: { clientId: string; me: Me }) {
  const { data, isLoading } = useQuery<Plan[]>({ queryKey: [`/api/clients/${clientId}/tax/estimates`] });
  const canEdit = hasPermission(me, "engagements.write");
  const [form, setForm] = useState<Plan | "new" | null>(null);
  const share = useMutation({ mutationFn: (id: string) => api.send("POST", `/api/tax/estimate-plans/${id}/share`) });
  const remove = useMutation({ mutationFn: (id: string) => api.send("DELETE", `/api/tax/estimate-plans/${id}`) });
  const today = localToday();
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setForm("new")}>
            Set up estimated payments
          </Button>
        </div>
      )}
      <SetAsidePanel clientId={clientId} canEdit={canEdit} />
      {!data?.length ? (
        <Empty>No estimated payment plans. Set one up from last year's return; once shared, the client sees each payment and confirms it in the portal.</Empty>
      ) : (
        <ShowMore
          items={data}
          initial={4}
          noun="plans"
          render={(p) => {
          const paid = p.payments.reduce((a, x) => a + (x.paidCents ?? 0), 0);
          const total = p.payments.reduce((a, x) => a + x.amountCents, 0);
          return (
            <div key={p.id} className="rounded-lg border border-border">
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-3">
                <p className="min-w-0 flex-1 text-sm font-medium">
                  {p.taxYear} {p.authority.name} · Form {p.authority.form}
                  <span className="block text-xs font-normal text-muted-foreground">
                    {p.basis === "custom" ? "Amounts set by the firm" : p.basis === "prior_year" ? "Safe harbor from last year's tax" : "Safe harbor from this year's projection"} · {formatCents(paid)} of {formatCents(total)} paid
                  </span>
                  {p.holidays && (
                    <span className={`block text-xs font-normal ${p.holidays.status === "confirmed" ? "text-muted-foreground" : "text-amber-700 dark:text-amber-300"}`}>
                      {p.holidays.name}: {ruleStatusLabel(p.holidays)}
                      {p.datesEstimated && " · the client sees these dates as estimates"}
                    </span>
                  )}
                  {p.datesEstimated && p.holidays?.status === "confirmed" && (
                    <span className="block text-xs font-normal text-muted-foreground">This plan was made before the holidays were confirmed. Set it up again to give the client confirmed dates.</span>
                  )}
                </p>
                {p.sharedAt ? <span className={`${pill} ${tone.done}`}>Shared with client</span> : <span className={`${pill} ${tone.off}`}>Not shared yet</span>}
                {canEdit && !p.sharedAt && (
                  <Button size="sm" onClick={() => share.mutate(p.id)} disabled={share.isPending}>
                    Share with client
                  </Button>
                )}
                {canEdit && !paid && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setForm(p)}>
                      Recalculate
                    </Button>
                    <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: "Delete this plan?", description: "The client will no longer see this schedule.", confirmLabel: "Delete", destructive: true })) && remove.mutate(p.id)}>
                      Delete
                    </Button>
                  </>
                )}
              </div>
              <ul className="divide-y divide-border px-4">
                {p.payments.map((x) => (
                  <PaymentRow key={x.id} p={x} canEdit={canEdit} today={today} />
                ))}
              </ul>
              {p.notes && <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">{p.notes}</p>}
            </div>
          );
          }}
        />
      )}
      {form && (
        <Dialog open onOpenChange={(o) => !o && setForm(null)}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{form === "new" ? "Set up estimated payments" : "Recalculate estimated payments"}</DialogTitle>
            </DialogHeader>
            <PlanForm clientId={clientId} plan={form === "new" ? undefined : form} onDone={() => setForm(null)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Firm-wide: estimated payments due recently or soon, and which clients have confirmed them. */
export function TaxEstimatesPage() {
  const { data, isLoading } = useQuery<Array<EstimatePayment & { clientId: string; clientName: string; taxYear: number; jurisdiction: string; shared: boolean }>>({ queryKey: ["/api/tax/estimates"] });
  const today = localToday();
  const rows = data ?? [];
  const unpaid = rows.filter((p) => !p.paidOn && p.amountCents > 0);
  const [search, setSearch] = useState("");
  const shown = rows.filter((p) => matches(search, p.clientName, ESTIMATE_AUTHORITIES[p.jurisdiction]?.name, String(p.taxYear)));
  const paged = usePaged(shown, { resetKey: search });
  return (
    <>
      <PageHeader crumbs={[{ label: "Tax", href: "/tax" }]} title="Estimated Payments" description="Installments due in the last 30 days and the next 45, and whether each client has confirmed paying." />
      <StatRow
        stats={[
          { label: "Past due, not confirmed", value: unpaid.filter((p) => p.dueOn < today).length, tone: unpaid.some((p) => p.dueOn < today) ? "danger" : "success" },
          { label: "Due in the next 14 days", value: unpaid.filter((p) => p.dueOn >= today && daysBetween(today, p.dueOn) <= 14).length, tone: "warning" },
          { label: "Confirmed paid", value: rows.filter((p) => p.paidOn).length, tone: "success" },
          { label: "Not shared with the client", value: rows.filter((p) => !p.shared).length },
        ]}
      />
      {isLoading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty>No estimated payments are due in this window. Set up a client's plan from their Tax tab.</Empty>
      ) : (
        <div className="space-y-3">
          <ListSearch value={search} onChange={setSearch} placeholder="Search by client" />
          {!shown.length ? (
            <Empty>No payments match the search.</Empty>
          ) : (
        <ul className="divide-y divide-border rounded-lg border border-card-border bg-card">
          {paged.items.map((p) => {
            const [label, cls] = paymentStatus(p, today);
            const a = ESTIMATE_AUTHORITIES[p.jurisdiction];
            return (
              <li key={p.id}>
                <Link href={`/clients/${p.clientId}?tab=tax`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-muted/30">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{p.clientName}</span>
                    <span className="text-xs text-muted-foreground">
                      {p.taxYear} {a?.name} · payment {p.installment} of 4{!p.shared && " · not shared"}
                    </span>
                  </span>
                  <span className="text-sm tabular">{formatDate(p.dueOn)}</span>
                  <span className="w-24 text-right text-sm font-medium tabular">{formatCents(p.amountCents)}</span>
                  <span className={`${pill} ${cls}`}>{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
          )}
          <Pager paged={paged} noun="payments" />
        </div>
      )}
    </>
  );
}

// --- Notices --------------------------------------------------------------------------------

export type Notice = {
  id: string;
  clientId: string;
  clientName?: string;
  authority: string;
  noticeCode: string;
  taxYear: number | null;
  noticeDate: string | null;
  receivedOn: string | null;
  responseDueOn: string | null;
  amountCents: number | null;
  summary: string;
  status: "new" | "open" | "responded" | "resolved";
  assignedUserId: string | null;
  noticeDocumentId: string | null;
  responseDocumentId: string | null;
  respondedOn: string | null;
  responseNote: string;
  resolvedOn: string | null;
  resolution: string;
  fromClient: boolean;
};

export function noticeStatus(n: Notice, today: string): [string, string] {
  if (n.status === "resolved") return [`Resolved ${formatDate(n.resolvedOn)}`, tone.done];
  if (n.status === "responded") return [`Responded ${formatDate(n.respondedOn)}`, tone.off];
  if (n.status === "new") return ["Sent by client", tone.wait];
  if (n.responseDueOn && n.responseDueOn < today) return ["Response overdue", tone.late];
  if (n.responseDueOn) return [`Respond by ${formatDate(n.responseDueOn)}`, tone.wait];
  return ["Open", tone.wait];
}

function NoticeDialog({ clientId, notice, onClose }: { clientId: string; notice?: Notice; onClose: () => void }) {
  const members = useMembers();
  const { data: docs } = useQuery<Doc[]>({ queryKey: ["/api/documents?clientId=", clientId] });
  const [v, setV] = useState({
    authority: notice?.authority ?? "federal",
    noticeCode: notice?.noticeCode ?? "",
    taxYear: notice?.taxYear ? String(notice.taxYear) : String(defaultTaxYear()),
    noticeDate: notice?.noticeDate ?? "",
    // null until typed: the suggested date stands in for it; cleared (""), it stays empty.
    responseDueOn: (notice ? (notice.responseDueOn ?? "") : null) as string | null,
    amount: dollars(notice?.amountCents),
    summary: notice?.summary ?? "",
    assignedUserId: notice?.assignedUserId ?? "",
    noticeDocumentId: notice?.noticeDocumentId ?? "",
  });
  // A notice must say which letter it is (the code or what it says) before it is saved.
  const [missing, setMissing] = useState(false);
  const needsDetail = !v.noticeCode.trim() && !v.summary.trim() && !(notice && v.noticeDocumentId);
  const suggested = v.authority === "federal" && v.noticeDate ? noticeResponseDate(v.noticeCode, v.noticeDate) : null;
  const known = IRS_NOTICES.find((x) => x.code.toLowerCase() === v.noticeCode.trim().toLowerCase());
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const body = {
        authority: v.authority,
        noticeCode: v.noticeCode,
        taxYear: v.taxYear ? Number(v.taxYear) : null,
        noticeDate: v.noticeDate || null,
        responseDueOn: (v.responseDueOn ?? suggested) || null,
        amountCents: money(v.amount),
        summary: v.summary,
        // A new notice left on "Me" goes to the person saving it; on an edit, "Unassigned" clears it.
        assignedUserId: notice ? v.assignedUserId || null : v.assignedUserId || undefined,
        noticeDocumentId: v.noticeDocumentId || null,
      };
      return notice ? api.send("PATCH", `/api/tax/notices/${notice.id}`, body) : api.send("POST", "/api/tax/notices", { ...body, clientId });
    },
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{notice ? "Notice details" : "Log a notice"}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (needsDetail) {
              setMissing(true);
              document.getElementById("n-code")?.focus();
              return;
            }
            save.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="n-auth">From</Label>
              <select id="n-auth" className={select} value={v.authority} onChange={(e) => setV({ ...v, authority: e.target.value })}>
                {Object.entries(NOTICE_AUTHORITIES).map(([k, name]) => (
                  <option key={k} value={k}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="n-code">Notice or letter</Label>
              <Input
                id="n-code"
                list="irs-notices"
                placeholder="e.g. CP2000"
                value={v.noticeCode}
                aria-invalid={missing && needsDetail}
                aria-describedby={missing && needsDetail ? "n-need" : undefined}
                onChange={(e) => setV({ ...v, noticeCode: e.target.value })}
              />
              <datalist id="irs-notices">
                {IRS_NOTICES.map((x) => (
                  <option key={x.code} value={x.code}>
                    {x.title}
                  </option>
                ))}
              </datalist>
            </div>
            <div className="space-y-1">
              <Label htmlFor="n-year">Tax year</Label>
              <Input id="n-year" inputMode="numeric" value={v.taxYear} onChange={(e) => setV({ ...v, taxYear: e.target.value })} />
            </div>
          </div>
          {known && (
            <p className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{known.title}.</span> {known.guidance}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="n-date">Date on the notice</Label>
              <Input id="n-date" type="date" value={v.noticeDate} onChange={(e) => setV({ ...v, noticeDate: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="n-due">Respond by</Label>
              <Input id="n-due" type="date" value={v.responseDueOn ?? suggested ?? ""} onChange={(e) => setV({ ...v, responseDueOn: e.target.value })} />
              {suggested && v.responseDueOn === null && <p className="text-xs text-muted-foreground">Suggested; confirm against the letter.</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="n-amt">Amount (if any)</Label>
              <Input id="n-amt" inputMode="decimal" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="n-who">Assigned to</Label>
              <select id="n-who" className={select} value={v.assignedUserId} onChange={(e) => setV({ ...v, assignedUserId: e.target.value })}>
                <option value="">{notice ? "Unassigned" : "Me"}</option>
                {members
                  .filter((m) => m.isActive && m.role !== "client" && m.role !== "readonly")
                  .map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="n-doc">The notice (document)</Label>
              <DocumentPicker id="n-doc" docs={docs} value={v.noticeDocumentId} onChange={(id) => setV({ ...v, noticeDocumentId: id })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="n-sum">What it says</Label>
            <Textarea
              id="n-sum"
              rows={2}
              value={v.summary}
              aria-invalid={missing && needsDetail}
              aria-describedby={missing && needsDetail ? "n-need" : undefined}
              onChange={(e) => setV({ ...v, summary: e.target.value })}
            />
          </div>
          {missing && needsDetail && (
            <p id="n-need" role="alert" className="text-sm text-destructive">
              Enter the notice code (for example CP2000) or a short summary of what it says, and attach the notice if you have it.
            </p>
          )}
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {notice ? "Save" : "Log notice"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Record the response, or the resolution. */
function NoticeStepDialog({ n, step, onClose }: { n: Notice; step: "responded" | "resolved"; onClose: () => void }) {
  const { data: docs } = useQuery<Doc[]>({ queryKey: ["/api/documents?clientId=", n.clientId] });
  const [v, setV] = useState({ date: localToday(), text: "", doc: "" });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send(
        "PATCH",
        `/api/tax/notices/${n.id}`,
        step === "responded" ? { status: "responded", respondedOn: v.date, responseNote: v.text, responseDocumentId: v.doc || null } : { status: "resolved", resolvedOn: v.date, resolution: v.text },
      ),
    onSuccess: onClose,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{step === "responded" ? "Record the response" : "Record the resolution"}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="ns-date">{step === "responded" ? "Sent on" : "Resolved on"}</Label>
            <Input id="ns-date" type="date" required value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} />
          </div>
          {step === "responded" && (
            <div className="space-y-1">
              <Label htmlFor="ns-doc">Response sent (document)</Label>
              <DocumentPicker id="ns-doc" docs={docs} value={v.doc} onChange={(id) => setV({ ...v, doc: id })} />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="ns-text">{step === "responded" ? "What was sent" : "Outcome"}</Label>
            <Textarea id="ns-text" rows={3} required={step === "resolved" || !v.doc} value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} placeholder={step === "responded" ? "e.g. Faxed Form 8949 with basis statements" : "e.g. IRS accepted the response; no change"} />
          </div>
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NoticeRow({ n, canEdit, showClient, today, focused }: { n: Notice; canEdit: boolean; showClient?: boolean; today: string; focused?: boolean }) {
  const names = useMemberNames();
  const [dialog, setDialog] = useState<"edit" | "responded" | "resolved" | null>(null);
  const reopen = useMutation({ mutationFn: () => api.send("PATCH", `/api/tax/notices/${n.id}`, { status: "open" }) });
  const [label, cls] = noticeStatus(n, today);
  return (
    <li className={cn("scroll-mt-20 px-4 py-3", focused && "bg-primary/5")} ref={(el) => {
        // Opened from a link to this notice: bring it into view once.
        if (focused && el && !el.dataset.shown) {
          el.dataset.shown = "1";
          el.scrollIntoView({ block: "center" });
        }
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-[1_1_20rem] text-sm">
          {showClient && (
            <Link href={`/clients/${n.clientId}?tab=tax`} className="font-medium hover:underline">
              {n.clientName}
              {" · "}
            </Link>
          )}
          <span className="font-medium">
            {NOTICE_AUTHORITIES[n.authority]} {n.noticeCode || "notice"}
          </span>
          {n.taxYear && <span className="text-muted-foreground"> · {n.taxYear}</span>}
          {n.amountCents ? <span className="text-muted-foreground tabular"> · {formatCents(n.amountCents)}</span> : null}
          <span className="block text-xs text-muted-foreground">
            {n.summary || (n.fromClient ? "Sent by the client from the portal" : "No summary")}
            {" · "}
            {n.assignedUserId ? (names.get(n.assignedUserId) ?? "—") : "Unassigned"}
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
        <span className={`${pill} ${cls}`}>{label}</span>
        {n.noticeDocumentId && (
          <a className="text-xs text-primary hover:underline" href={`/api/documents/${n.noticeDocumentId}/download`}>
            Notice
          </a>
        )}
        {n.responseDocumentId && (
          <a className="text-xs text-primary hover:underline" href={`/api/documents/${n.responseDocumentId}/download`}>
            Response
          </a>
        )}
        {canEdit && (
          <span className="flex gap-1">
            {(n.status === "new" || n.status === "open") && (
              <Button size="sm" variant="outline" onClick={() => setDialog("responded")}>
                Record response
              </Button>
            )}
            {n.status !== "resolved" && (
              <Button size="sm" variant="outline" onClick={() => setDialog("resolved")}>
                Resolve
              </Button>
            )}
            {n.status === "resolved" ? (
              <Button size="sm" variant="ghost" onClick={() => reopen.mutate()}>
                Reopen
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setDialog("edit")}>
                {n.status === "new" ? "Take on" : "Edit"}
              </Button>
            )}
          </span>
        )}
        </div>
      </div>
      {(n.responseNote || n.resolution) && (
        <p className="mt-1 text-xs text-muted-foreground">
          {n.responseNote && `Response: ${n.responseNote}`}
          {n.responseNote && n.resolution && " · "}
          {n.resolution && `Outcome: ${n.resolution}`}
        </p>
      )}
      {dialog === "edit" && <NoticeDialog clientId={n.clientId} notice={n} onClose={() => setDialog(null)} />}
      {(dialog === "responded" || dialog === "resolved") && <NoticeStepDialog n={n} step={dialog} onClose={() => setDialog(null)} />}
    </li>
  );
}

/** A client's notices, newest first. */
export function NoticesPanel({ clientId, me }: { clientId: string; me: Me }) {
  const { data, isLoading } = useQuery<Notice[]>({ queryKey: [`/api/tax/notices?clientId=${clientId}&status=all`] });
  const [adding, setAdding] = useState(false);
  const canEdit = hasPermission(me, "engagements.write");
  const today = localToday();
  const paged = usePaged(data ?? []);
  return (
    <div className="space-y-3">
      {canEdit && !!data?.length && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setAdding(true)}>
            Log a notice
          </Button>
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        // Nothing logged: the message and the button share one quiet row.
        <div className="flex flex-col gap-3 rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>No IRS or state notices. {canEdit ? "Log one here, or the client can send one from the portal." : "The client can send one from the portal."}</p>
          {canEdit && (
            <Button size="sm" className="shrink-0 self-start sm:self-auto" onClick={() => setAdding(true)}>
              Log a notice
            </Button>
          )}
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {paged.items.map((n) => (
              <NoticeRow key={n.id} n={n} canEdit={canEdit} today={today} />
            ))}
          </ul>
          <Pager paged={paged} noun="notices" />
        </>
      )}
      {adding && <NoticeDialog clientId={clientId} onClose={() => setAdding(false)} />}
    </div>
  );
}

/** One group of the firm-wide queue, 10 at a time; opens on the page holding a linked notice. */
function NoticeGroup({ title, list, canEdit, today, focus, resetKey }: { title: string; list: Notice[]; canEdit: boolean; today: string; focus: string | null; resetKey: string }) {
  const paged = usePaged(list, { resetKey });
  const at = focus ? list.findIndex((n) => n.id === focus) : -1;
  const { setPage, size } = paged;
  useEffect(() => {
    if (at >= 0) setPage(Math.floor(at / size));
  }, [at, size, setPage]);
  return (
    <Section title={title} description={`${list.length} notice${list.length === 1 ? "" : "s"}`}>
      <div className="-m-5">
        <ul className="divide-y divide-border">
          {paged.items.map((n) => (
            <NoticeRow key={n.id} n={n} canEdit={canEdit} showClient today={today} focused={focus === n.id} />
          ))}
        </ul>
        <Pager paged={paged} noun="notices" className="px-4 pb-3" />
      </div>
    </Section>
  );
}

/** Firm-wide notice queue: new from clients, open by response date, then responded. */
export function TaxNoticesPage({ me }: { me: Me }) {
  const search = new URLSearchParams(useSearch());
  const [all, setAll] = useState(false);
  const { data, isLoading } = useQuery<Notice[]>({ queryKey: [`/api/tax/notices?status=${all ? "all" : "active"}`] });
  const canEdit = hasPermission(me, "engagements.write");
  const today = localToday();
  const [q, setQ] = useState("");
  const rows = (data ?? []).filter((n) => matches(q, n.clientName, n.noticeCode, n.summary, NOTICE_AUTHORITIES[n.authority]));
  const focus = search.get("notice");
  const groups: Array<[string, Notice[]]> = [
    ["Sent by clients", rows.filter((n) => n.status === "new")],
    ["Open", rows.filter((n) => n.status === "open")],
    ["Responded, awaiting the agency", rows.filter((n) => n.status === "responded")],
    ...(all ? [["Resolved", rows.filter((n) => n.status === "resolved")] as [string, Notice[]]] : []),
  ];
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Tax", href: "/tax" }]}
        title="IRS & State Notices"
        description="Every notice, who has it, and when the response is due. Log a notice from the client's Tax tab."
        actions={
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show resolved
          </label>
        }
      />
      <StatRow
        stats={[
          { label: "Response overdue", value: (data ?? []).filter((n) => n.status === "open" && n.responseDueOn && n.responseDueOn < today).length, tone: (data ?? []).some((n) => n.status === "open" && n.responseDueOn && n.responseDueOn < today) ? "danger" : "success" },
          { label: "Due in the next 14 days", value: (data ?? []).filter((n) => n.status === "open" && n.responseDueOn && n.responseDueOn >= today && daysBetween(today, n.responseDueOn) <= 14).length, tone: "warning" },
          { label: "Sent by clients", value: (data ?? []).filter((n) => n.status === "new").length, tone: (data ?? []).some((n) => n.status === "new") ? "warning" : "default" },
          { label: "Awaiting the agency", value: (data ?? []).filter((n) => n.status === "responded").length },
        ]}
      />
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>{all ? "No notices yet." : "No open notices."}</Empty>
      ) : (
        <div className="space-y-5">
          <ListSearch value={q} onChange={setQ} placeholder="Search by client, notice or summary" />
          {!rows.length && <Empty>No notices match the search.</Empty>}
          {groups
            .filter(([, list]) => list.length)
            .map(([title, list]) => (
              <NoticeGroup key={title} title={title} list={list} canEdit={canEdit} today={today} focus={focus} resetKey={q} />
            ))}
        </div>
      )}
    </>
  );
}

export { jurisdictionName };
